import { useMemo } from 'react';

import type PERMISSIONS from '@snowit/utilities/permissions';
import type { MainRoute, NavOptions, NavRoute } from './types';

import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';

import navGroups from './config';
import { buildBreadcrumbs, findActiveRoute, flattenGroups } from './utils';

import QueryString from 'qs';
import { useLocation, useParams, useRoute, useSearch } from 'wouter';

const allRoutes = flattenGroups(navGroups);

export const useBreadcrumbs = (): NavRoute[] => {
  const { t, language } = useLanguage();
  const [location] = useLocation();

  return useMemo(() => buildBreadcrumbs(location, allRoutes, t), [location, language, t]);
};

export const useRouter = () => {
  const search = useSearch();
  const { authType } = useAuth();
  const useQuery = <T extends { [x: string]: unknown }>(): T => {
    return QueryString.parse(search) as T;
  };
  const [location, navigate] = useLocation();

  const activeRoute = useMemo(() => findActiveRoute(location, allRoutes), [location, allRoutes]);

  const useURL = <T extends string[]>(): T => {
    return location.replace(/^\//, '').split('/') as T;
  };

  const findByPath = (path: string): NavRoute | undefined => {
    return allRoutes.find((route) => route.path === path);
  };

  const findPermissionByPath = (path: string): PERMISSIONS[] => {
    const found = findByPath(path);
    if (!found) return [];
    if (found.type === 0 && authType !== 'ADMIN') return ['ONLY_ADMIN' as PERMISSIONS];
    return found.permissions ?? [];
  };

  const nav = (opts: MainRoute | NavOptions, ...rest: (string | number | undefined | null)[]) => {
    let path: string[];
    let replace = false;
    if (typeof opts === 'string') {
      path = [opts, ...(rest ?? [])].filter(Boolean).map(String);
    } else {
      path = opts.path.filter(Boolean).map(String);
      if (opts.querystring) {
        path = [`${path.join('/')}?${QueryString.stringify(opts.querystring)}`];
      }
      replace = Boolean(opts.replace);
      if (opts.target === '_blank') {
        window.open(`/${path.join('/')}`, '_blank');
        return;
      }
    }
    navigate(`/${path.join('/')}`, { replace });
  };

  const getLink = (opts: NavOptions): string => {
    let path: string[] = opts.path.filter(Boolean).map(String);
    if (opts.querystring) {
      path = [`${path.join('/')}?${QueryString.stringify(opts.querystring)}`];
    }
    if (opts.absolute) {
      return `${window.location.protocol}//${window.location.host}/${path.join('/')}`;
    }
    return `/${path.join('/')}`;
  };

  const goBack = () => {
    window.history.back();
  };

  const pushHistory = (path: string) => navigate(path);

  return {
    useParams,
    useSearch,
    useLocation,
    useQuery,
    useRoute,
    location,
    navigate_raw: navigate,
    navigate: nav,
    nav,
    findByPath,
    findPermissionByPath,
    useURL,
    goBack,
    getLink,
    pushHistory,
    allRoutes,
    activeRoute,
  };
};
